
import { Ball, SimulationConfig, BallStyle, BallTexture } from '../../types';
import { ShapeUtils } from '../../logic/physics/ShapeUtils';

export const drawAdvancedBall = (
  ctx: CanvasRenderingContext2D,
  b: Ball,
  config: SimulationConfig,
  rgb: string
) => {
  const r = b.radius;
  const shape = b.shape || 'CIRCLE';
  const isBox = b.isBox;
  const rot = b.angle || 0;
  const style = config.ballStyle || 'SHINY';
  const texture = config.ballTexture || 'NONE';
  const textureOpacity = config.ballTextureOpacity || 0.5;

  ctx.save();
  ctx.translate(b.pos.x, b.pos.y);
  ctx.rotate(rot);

  // 1. Trace Path
  ctx.beginPath();
  if (shape === 'CIRCLE' && !isBox) {
    ctx.arc(0, 0, r, 0, Math.PI * 2);
  } else if (shape === 'SQUARE' || isBox) {
    const w = b.dimensions?.x || r * 2;
    const h = b.dimensions?.y || r * 2;
    ctx.rect(-w / 2, -h / 2, w, h);
  } else {
    ShapeUtils.traceShape(ctx, {x:0, y:0}, shape, r);
  }

  // 2. Apply Base Color/Style
  applyBallStyle(ctx, r, style, rgb, config);

  if (style !== 'NONE') {
    // 2.5 Subtle Inner Shadow for depth
    const innerShadow = ctx.createRadialGradient(0, 0, r * 0.8, 0, 0, r);
    innerShadow.addColorStop(0, 'transparent');
    innerShadow.addColorStop(1, 'rgba(0,0,0,0.2)');
    ctx.fillStyle = innerShadow;
    ctx.fill();

    // 2.6 Top Highlight
    const highlight = ctx.createRadialGradient(-r * 0.3, -r * 0.3, 0, -r * 0.3, -r * 0.3, r * 0.5);
    highlight.addColorStop(0, 'rgba(255,255,255,0.4)');
    highlight.addColorStop(1, 'transparent');
    ctx.fillStyle = highlight;
    ctx.fill();
  }

  // 3. Apply Texture Overlay
  if (texture !== 'NONE') {
    ctx.save();
    ctx.clip();
    drawBallTexture(ctx, r, texture, textureOpacity, rgb);
    ctx.restore();
  }

  // 4. Stroke
  if (config.ballStrokeWidth > 0) {
    ctx.strokeStyle = config.ballStrokeColor;
    ctx.globalAlpha = config.ballStrokeOpacity || 1;
    ctx.lineWidth = config.ballStrokeWidth;
    ctx.stroke();
  }

  // 5. Glow
  if (config.ballGlowRadius > 0) {
    ctx.save();
    ctx.shadowBlur = config.ballGlowRadius;
    ctx.shadowColor = config.ballGlowColor || rgb;
    ctx.globalAlpha = config.ballGlowOpacity || 0.5;
    ctx.stroke();
    ctx.restore();
  }

  // 6. Eye for boxes
  if (isBox) {
      ctx.fillStyle = 'white';
      ctx.beginPath();
      ctx.arc(r * 0.4, 0, r * 0.2, 0, Math.PI * 2);
      ctx.fill();
  }

  ctx.restore();
};

const applyBallStyle = (ctx: CanvasRenderingContext2D, r: number, style: BallStyle, rgb: string, config: SimulationConfig) => {
  ctx.globalAlpha = config.ballFillOpacity || 1;

  switch (style) {
    case 'SHINY': {
      const grad = ctx.createRadialGradient(-r * 0.3, -r * 0.3, r * 0.1, 0, 0, r);
      grad.addColorStop(0, '#fff');
      grad.addColorStop(0.2, rgb);
      grad.addColorStop(1, 'rgba(0,0,0,0.5)');
      ctx.fillStyle = grad;
      ctx.fill();
      break;
    }
    case 'NEON': {
      ctx.fillStyle = 'rgba(0,0,0,0.8)';
      ctx.fill();
      ctx.strokeStyle = rgb;
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.shadowBlur = 15;
      ctx.shadowColor = rgb;
      ctx.stroke();
      break;
    }
    case 'CHROME': {
      const grad = ctx.createLinearGradient(-r, -r, r, r);
      grad.addColorStop(0, '#fff');
      grad.addColorStop(0.4, rgb);
      grad.addColorStop(0.5, '#000');
      grad.addColorStop(0.6, rgb);
      grad.addColorStop(1, '#fff');
      ctx.fillStyle = grad;
      ctx.fill();
      break;
    }
    case 'MATTE': {
      ctx.fillStyle = rgb;
      ctx.fill();
      // Subtle top lighting
      const grad = ctx.createRadialGradient(0, -r, 0, 0, -r, r * 2);
      grad.addColorStop(0, 'rgba(255,255,255,0.2)');
      grad.addColorStop(1, 'transparent');
      ctx.fillStyle = grad;
      ctx.fill();
      break;
    }
    case 'GHOST': {
      ctx.globalAlpha *= 0.3;
      ctx.fillStyle = rgb;
      ctx.fill();
      ctx.strokeStyle = rgb;
      ctx.lineWidth = 1;
      ctx.stroke();
      break;
    }
    case 'CRYSTAL': {
      ctx.fillStyle = 'rgba(255,255,255,0.1)';
      ctx.fill();
      const grad = ctx.createLinearGradient(-r, -r, r, r);
      grad.addColorStop(0, 'rgba(255,255,255,0.8)');
      grad.addColorStop(0.5, rgb);
      grad.addColorStop(1, 'rgba(255,255,255,0.8)');
      ctx.strokeStyle = grad;
      ctx.lineWidth = 2;
      ctx.stroke();
      // Facets
      ctx.beginPath();
      ctx.moveTo(-r * 0.5, -r * 0.5);
      ctx.lineTo(r * 0.5, r * 0.5);
      ctx.stroke();
      break;
    }
    case 'METAL': {
      const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
      grad.addColorStop(0, '#eee');
      grad.addColorStop(0.7, rgb);
      grad.addColorStop(1, '#333');
      ctx.fillStyle = grad;
      ctx.fill();
      break;
    }
    case 'GLOW': {
      const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
      grad.addColorStop(0, '#fff');
      grad.addColorStop(0.5, rgb);
      grad.addColorStop(1, 'transparent');
      ctx.fillStyle = grad;
      ctx.fill();
      break;
    }
    case 'WIREFRAME': {
      ctx.strokeStyle = rgb;
      ctx.lineWidth = 1;
      ctx.stroke();
      // Grid lines
      ctx.beginPath();
      for (let i = -r; i <= r; i += r / 4) {
        ctx.moveTo(i, -Math.sqrt(r * r - i * i));
        ctx.lineTo(i, Math.sqrt(r * r - i * i));
        ctx.moveTo(-Math.sqrt(r * r - i * i), i);
        ctx.lineTo(Math.sqrt(r * r - i * i), i);
      }
      ctx.stroke();
      break;
    }
    case 'HOLOGRAPHIC': {
      const grad = ctx.createLinearGradient(-r, 0, r, 0);
      grad.addColorStop(0, 'rgba(255,0,0,0.5)');
      grad.addColorStop(0.2, 'rgba(255,255,0,0.5)');
      grad.addColorStop(0.4, 'rgba(0,255,0,0.5)');
      grad.addColorStop(0.6, 'rgba(0,255,255,0.5)');
      grad.addColorStop(0.8, 'rgba(0,0,255,0.5)');
      grad.addColorStop(1, 'rgba(255,0,255,0.5)');
      ctx.fillStyle = grad;
      ctx.fill();
      ctx.strokeStyle = rgb;
      ctx.stroke();
      break;
    }
    case 'PLASMA': {
      const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
      grad.addColorStop(0, rgb);
      grad.addColorStop(0.5, '#ff00ff');
      grad.addColorStop(1, '#0000ff');
      ctx.fillStyle = grad;
      ctx.fill();
      break;
    }
    case 'FROSTED': {
      ctx.fillStyle = 'rgba(255,255,255,0.8)';
      ctx.fill();
      ctx.fillStyle = rgb;
      ctx.globalAlpha *= 0.4;
      ctx.fill();
      break;
    }
    case 'GLASS': {
      ctx.fillStyle = 'rgba(255,255,255,0.1)';
      ctx.fill();
      const grad = ctx.createRadialGradient(-r * 0.4, -r * 0.4, r * 0.1, -r * 0.4, -r * 0.4, r * 0.8);
      grad.addColorStop(0, 'rgba(255,255,255,0.8)');
      grad.addColorStop(1, 'transparent');
      ctx.fillStyle = grad;
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.5)';
      ctx.stroke();
      break;
    }
    case 'PEARL': {
      const grad = ctx.createRadialGradient(-r * 0.3, -r * 0.3, 0, 0, 0, r);
      grad.addColorStop(0, '#fff');
      grad.addColorStop(0.5, rgb);
      grad.addColorStop(1, '#ddd');
      ctx.fillStyle = grad;
      ctx.fill();
      break;
    }
    case 'IRIDESCENT': {
      const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
      grad.addColorStop(0, rgb);
      grad.addColorStop(0.5, '#0ff');
      grad.addColorStop(1, '#f0f');
      ctx.fillStyle = grad;
      ctx.fill();
      break;
    }
    case 'LAVA': {
      ctx.fillStyle = '#300';
      ctx.fill();
      const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
      grad.addColorStop(0, '#f90');
      grad.addColorStop(0.5, rgb);
      grad.addColorStop(1, 'transparent');
      ctx.fillStyle = grad;
      ctx.fill();
      break;
    }
    case 'ELECTRIC': {
      ctx.fillStyle = '#002';
      ctx.fill();
      ctx.strokeStyle = rgb;
      ctx.lineWidth = 2;
      ctx.shadowBlur = 10;
      ctx.shadowColor = rgb;
      ctx.stroke();
      break;
    }
    case 'CARBON': {
      ctx.fillStyle = '#111';
      ctx.fill();
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 1;
      ctx.stroke();
      break;
    }
    case 'WOOD': {
      ctx.fillStyle = '#5d4037';
      ctx.fill();
      break;
    }
    case 'MARBLE': {
      ctx.fillStyle = '#e0e0e0';
      ctx.fill();
      break;
    }
    case 'GOLD': {
      const grad = ctx.createLinearGradient(-r, -r, r, r);
      grad.addColorStop(0, '#ffd700');
      grad.addColorStop(0.5, '#b8860b');
      grad.addColorStop(1, '#ffd700');
      ctx.fillStyle = grad;
      ctx.fill();
      break;
    }
    case 'SILVER': {
      const grad = ctx.createLinearGradient(-r, -r, r, r);
      grad.addColorStop(0, '#c0c0c0');
      grad.addColorStop(0.5, '#808080');
      grad.addColorStop(1, '#c0c0c0');
      ctx.fillStyle = grad;
      ctx.fill();
      break;
    }
    case 'COPPER': {
      const grad = ctx.createLinearGradient(-r, -r, r, r);
      grad.addColorStop(0, '#b87333');
      grad.addColorStop(0.5, '#8b4513');
      grad.addColorStop(1, '#b87333');
      ctx.fillStyle = grad;
      ctx.fill();
      break;
    }
    case 'RUBY': {
      const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
      grad.addColorStop(0, '#ff0000');
      grad.addColorStop(1, '#800000');
      ctx.fillStyle = grad;
      ctx.fill();
      break;
    }
    case 'EMERALD': {
      const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
      grad.addColorStop(0, '#50c878');
      grad.addColorStop(1, '#043927');
      ctx.fillStyle = grad;
      ctx.fill();
      break;
    }
    case 'SAPPHIRE': {
      const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
      grad.addColorStop(0, '#0f52ba');
      grad.addColorStop(1, '#000080');
      ctx.fillStyle = grad;
      ctx.fill();
      break;
    }
    default:
      ctx.fillStyle = rgb;
      ctx.fill();
  }
};

const drawBallTexture = (ctx: CanvasRenderingContext2D, r: number, texture: BallTexture, opacity: number, rgb: string) => {
  ctx.globalAlpha = opacity;
  ctx.strokeStyle = rgb;
  ctx.fillStyle = rgb;
  ctx.lineWidth = 1;

  switch (texture) {
    case 'STRIPES':
      for (let i = -r; i <= r; i += 4) {
        ctx.beginPath();
        ctx.moveTo(i, -r);
        ctx.lineTo(i, r);
        ctx.stroke();
      }
      break;
    case 'DOTS':
      for (let x = -r; x <= r; x += 6) {
        for (let y = -r; y <= r; y += 6) {
          ctx.beginPath();
          ctx.arc(x, y, 1, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      break;
    case 'CROSSHATCH':
      for (let i = -r; i <= r; i += 5) {
        ctx.beginPath();
        ctx.moveTo(i, -r); ctx.lineTo(i, r);
        ctx.moveTo(-r, i); ctx.lineTo(r, i);
        ctx.stroke();
      }
      break;
    case 'NOISE':
      for (let i = 0; i < 100; i++) {
        ctx.beginPath();
        ctx.arc((Math.random() - 0.5) * 2 * r, (Math.random() - 0.5) * 2 * r, 0.5, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    case 'CHECKERBOARD':
      for (let x = -r; x <= r; x += 8) {
        for (let y = -r; y <= r; y += 8) {
          if (((x + r) / 8 + (y + r) / 8) % 2 === 0) {
            ctx.fillRect(x, y, 8, 8);
          }
        }
      }
      break;
    case 'HONEYCOMB':
      for (let x = -r; x <= r; x += 10) {
        for (let y = -r; y <= r; y += 8) {
          ctx.beginPath();
          const ox = (y / 8) % 2 === 0 ? 0 : 5;
          ctx.arc(x + ox, y, 3, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
      break;
    case 'CIRCUIT':
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        ctx.moveTo((Math.random() - 0.5) * 2 * r, (Math.random() - 0.5) * 2 * r);
        ctx.lineTo((Math.random() - 0.5) * 2 * r, (Math.random() - 0.5) * 2 * r);
        ctx.stroke();
      }
      break;
    case 'WAVES':
      for (let y = -r; y <= r; y += 6) {
        ctx.beginPath();
        for (let x = -r; x <= r; x += 2) {
          ctx.lineTo(x, y + Math.sin(x * 0.2) * 2);
        }
        ctx.stroke();
      }
      break;
    case 'STARS':
      for (let i = 0; i < 10; i++) {
        const x = (Math.random() - 0.5) * 2 * r;
        const y = (Math.random() - 0.5) * 2 * r;
        ctx.beginPath();
        ctx.moveTo(x, y - 3); ctx.lineTo(x, y + 3);
        ctx.moveTo(x - 3, y); ctx.lineTo(x + 3, y);
        ctx.stroke();
      }
      break;
    case 'BRICKS':
      for (let y = -r; y <= r; y += 6) {
        ctx.beginPath();
        ctx.moveTo(-r, y); ctx.lineTo(r, y);
        ctx.stroke();
        const ox = (y / 6) % 2 === 0 ? 0 : 5;
        for (let x = -r + ox; x <= r; x += 10) {
          ctx.beginPath();
          ctx.moveTo(x, y); ctx.lineTo(x, y + 6);
          ctx.stroke();
        }
      }
      break;
    case 'SWIRL':
      ctx.beginPath();
      for (let a = 0; a < Math.PI * 10; a += 0.1) {
        const dist = a * 0.5;
        if (dist > r) break;
        ctx.lineTo(Math.cos(a) * dist, Math.sin(a) * dist);
      }
      ctx.stroke();
      break;
    case 'CLOUD':
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        ctx.arc((Math.random() - 0.5) * r, (Math.random() - 0.5) * r, r * 0.3, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    case 'FIRE':
      for (let i = 0; i < 10; i++) {
        ctx.beginPath();
        ctx.moveTo((Math.random() - 0.5) * 2 * r, r);
        ctx.lineTo((Math.random() - 0.5) * 2 * r, -r);
        ctx.stroke();
      }
      break;
    case 'WATER':
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        ctx.ellipse((Math.random() - 0.5) * r, (Math.random() - 0.5) * r, r * 0.4, r * 0.1, Math.random() * Math.PI, 0, Math.PI * 2);
        ctx.stroke();
      }
      break;
    case 'LEAF':
      for (let i = 0; i < 8; i++) {
        ctx.beginPath();
        ctx.ellipse((Math.random() - 0.5) * r, (Math.random() - 0.5) * r, r * 0.2, r * 0.05, Math.random() * Math.PI, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    case 'ZEBRA':
      for (let i = -r; i <= r; i += 8) {
        ctx.beginPath();
        ctx.lineWidth = 4;
        ctx.moveTo(i, -r); ctx.lineTo(i + 4, r);
        ctx.stroke();
      }
      break;
    case 'LEOPARD':
      for (let i = 0; i < 20; i++) {
        ctx.beginPath();
        ctx.arc((Math.random() - 0.5) * 2 * r, (Math.random() - 0.5) * 2 * r, 2, 0, Math.PI * 2);
        ctx.stroke();
      }
      break;
    case 'SNAKE':
      for (let i = -r; i <= r; i += 10) {
        for (let j = -r; j <= r; j += 10) {
          ctx.beginPath();
          ctx.rect(i, j, 5, 5);
          ctx.stroke();
        }
      }
      break;
    case 'GALAXY':
      for (let i = 0; i < 50; i++) {
        const a = Math.random() * Math.PI * 2;
        const d = Math.random() * r;
        ctx.beginPath();
        ctx.arc(Math.cos(a) * d, Math.sin(a) * d, 0.5, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    case 'MATRIX':
      ctx.font = '4px monospace';
      for (let i = -r; i <= r; i += 5) {
        for (let j = -r; j <= r; j += 5) {
          ctx.fillText(Math.random() > 0.5 ? '1' : '0', i, j);
        }
      }
      break;
    case 'TECH':
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        ctx.rect((Math.random() - 0.5) * 2 * r, (Math.random() - 0.5) * 2 * r, 4, 4);
        ctx.stroke();
      }
      break;
    case 'RETRO':
      for (let i = -r; i <= r; i += 10) {
        ctx.beginPath();
        ctx.moveTo(-r, i); ctx.lineTo(r, i);
        ctx.stroke();
      }
      break;
    case 'VINTAGE':
      ctx.globalAlpha *= 0.5;
      ctx.fillStyle = '#f4e4bc';
      ctx.fillRect(-r, -r, r * 2, r * 2);
      break;
  }
};
